import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { initAnalytics } from '@cw-elements/analytics-provider';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef, useState } from 'react';

import { DATADOG_RUM_CONFIG } from '../datadog/datadog-rum.config';
import { TRACKING_KEY, TRACKING_OPTIONS } from './tracking.constant';

/**
 * Boots Amplitude and Datadog RUM through the analytics provider, which owns the
 * consent lifecycle (CookieBot / `has_cookies_consent` query param / `cw_consent`
 * cookie), holds the real user id until consent is granted and stamps every event
 * with `analytics_consent`.
 *
 * `analyticsProviderConsentEnabled: false` runs the provider's legacy path — Amplitude
 * initializes as before, with no consent manager and no IP suppression. That is the
 * documented rollback, so the flag has to be resolved before we initialize: reading it
 * too early would return `undefined` and silently pin every session to legacy mode.
 */
const useInitAnalytics = () => {
  const { analyticsProviderConsentEnabled } = useFlags();
  const ldClient = useLDClient();
  const [isFlagsReady, setIsFlagsReady] = useState(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    let isCancelled = false;

    ldClient
      .waitUntilReady()
      .catch(() => undefined)
      .then(() => {
        if (!isCancelled) {
          setIsFlagsReady(true);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [ldClient]);

  useEffect(() => {
    if (!isFlagsReady || hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    initAnalytics({
      ...(TRACKING_KEY && {
        amplitude: {
          clientId: TRACKING_KEY,
          autocapture: false,
          options: TRACKING_OPTIONS,
        },
      }),
      datadog: DATADOG_RUM_CONFIG,
      consent: { enabled: !!analyticsProviderConsentEnabled },
    }).then((amplitude) => {
      // Guides & Surveys accepts a single plugin instance, registered by the host app.
      amplitude?.add(engagementPlugin());
    });
  }, [isFlagsReady, analyticsProviderConsentEnabled]);
};

export { useInitAnalytics };
